import React, { Fragment, useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import CustomDropdown from "@/components/common/custom-dropdown/custom-dropdown";
import useDropdownDisclosure from "@/components/common/custom-dropdown/hooks/useDropdownDisclosure";
import { findIndex, find, get, sortBy } from "lodash";
import OneSignal from "react-onesignal";
import { SUPPORTED_MESSAGE_TYPES, NOTIF_TYPE_SORT_ORDER } from "@/components/constants/notification-settings.constants";
import Switch from "@/components/common/switch/switch";
import { clsx } from "clsx";

interface NotifActionProps {
    frequency_selected: string;
    notif_type: string;
    index: string;
    message_type: string;
    handleSetting: (settings: { message_type: string; notif_type: string; frequency_selected: string }) => void;
    notification_frequencies: any;
    has_frequency_label: boolean;
    action_type?: "dropdown" | "switch";
}

interface NotificationSettingProps {
    notificationSetting: any[];
    setProfileValues: Dispatch<SetStateAction<any>>;
    gameVersion?: string;
    notifyDailyProperty?: boolean;
}

interface DropdownItem {
    name: string | number;
    value: string | number;
}

const NotifAction: React.FC<NotifActionProps> = (props) => {
    const {
        frequency_selected,
        notif_type,
        index,
        message_type,
        handleSetting,
        notification_frequencies,
        has_frequency_label,
        action_type = "dropdown", // dropdown, switch
    } = props;

    const { dropdownRef, openDropdown, handleOpen } = useDropdownDisclosure();

    /**
     * Get the notification label based on the frequency name.
     *
     * @param {string} frequency_name - The name of the frequency.
     * @returns {string} - The corresponding notification label.
     */
    const getNotifLabel = (frequency_name: string): string => {
        if (has_frequency_label) {
            if (frequency_name === "daily") return "On";
            if (frequency_name === "never") return "Off";
        }
        return frequency_name;
    };

    // Return dropdown component if action type is dropdown
    if (action_type === "dropdown") {
        return (
            <div className="grid-item" key={`${index}-${notif_type}`}>
                <CustomDropdown
                    key={index}
                    dropdownRef={dropdownRef as React.RefObject<HTMLDivElement>}
                    open={openDropdown}
                    selected={getNotifLabel(frequency_selected)}
                    handleOpen={handleOpen}
                    items={notification_frequencies}
                    handleSelect={(frequency: DropdownItem) => {
                        let frequency_name = has_frequency_label ? String(frequency.value) : String(frequency.name);
                        // Open OneSignal subscribe prompt if frequence is weekly or daily and notif type is push
                        if (
                            (frequency_name === "weekly" || frequency_name === "daily") &&
                            notif_type === "pushFrequency"
                        ) {
                            OneSignal.Slidedown.promptPush(); // Does not prompt if user is already subscribed
                            OneSignal.User.addTag("frequency", frequency_name); // Adds frequency tag to user
                        }
                        handleSetting({ message_type, notif_type, frequency_selected: frequency_name });
                        handleOpen();
                    }}
                />
            </div>
        );
    }

    // Return switch component if action type is switch
    if (action_type === "switch") {
        return (
            <div className="grid-item p-0 lh-1" key={`${index}-${notif_type}`}>
                <Switch
                    checked={frequency_selected === "daily"}
                    onChange={(e) => {
                        const selected = e.target.checked ? "daily" : "never";
                        handleSetting({ message_type, notif_type, frequency_selected: selected });
                    }}
                />
            </div>
        );
    }

    return null;
};

/**
 * Adds missing settings for supported message types.
 *
 * @param {Array} settings - The array of existing settings.
 * @param {string} gameVersion - The game version.
 * @returns {Array} - The updated array of settings with missing ones added.
 */
const addMissingSetting = (settings: any[], gameVersion: string): any[] => {
    const messageSettings = SUPPORTED_MESSAGE_TYPES[gameVersion as keyof typeof SUPPORTED_MESSAGE_TYPES];
    const missingSettings = messageSettings.filter((type: any) => {
        return !find(settings, { messageType: type.message_type });
    });

    // Add additional setting values to the user settings (like the setting's action type)
    const addedSettings = settings.map((setting: any) => {
        const supportedMessageTypeDefinition = find(
            SUPPORTED_MESSAGE_TYPES[gameVersion as keyof typeof SUPPORTED_MESSAGE_TYPES],
            {
                message_type: setting.messageType,
            },
        );

        return {
            ...setting,
            ...(supportedMessageTypeDefinition?.action_type && {
                actionType: supportedMessageTypeDefinition.action_type,
            }),
        };
    });

    // Transform missing settings to match the structure of existing settings (messageType instead of message_type)
    const transformedMissingSettings = missingSettings.map((setting: any) => ({
        ...setting,
        messageType: setting.message_type,
    }));

    const completedSettings = [...addedSettings, ...transformedMissingSettings];

    const notifSortOrder = NOTIF_TYPE_SORT_ORDER(gameVersion);

    // `completedSettings` always has `messageType` (existing + transformed missing settings),
    // so we can sort directly on that field.
    return sortBy(completedSettings, (setting: any) => notifSortOrder[setting.messageType]);
};

const NotificationSetting: React.FC<NotificationSettingProps> = ({
    notificationSetting,
    setProfileValues,
    gameVersion = "classic",
    notifyDailyProperty,
}) => {
    // Compute initial + merged settings only when the incoming props change
    const initialSetting = useMemo(
        () => addMissingSetting(notificationSetting ?? [], gameVersion),
        [notificationSetting, gameVersion],
    );

    const [notificationSettings, setNotificationSettings] = useState(initialSetting);

    // Only update local state when the prop changes (from parent), not when local state changes
    useEffect(() => {
        setNotificationSettings(initialSetting);
    }, [initialSetting]);

    /**
     * Handles updating user notification settings based on the provided parameters.
     *
     * @param {Object} settings - Settings object
     * @param {string} settings.message_type - The type of the message.
     * @param {string} settings.notif_type - The type of the notification.
     * @param {string} settings.frequency_selected - The selected frequency for the notification.
     */
    const handleSetting = ({
        message_type,
        notif_type,
        frequency_selected,
    }: {
        message_type: string;
        notif_type: string;
        frequency_selected: string;
    }) => {
        // pass by value
        const clonedSetting = [...notificationSettings];
        // find message type
        const foundSettingIndex = findIndex(clonedSetting, { messageType: message_type });

        if (foundSettingIndex > -1) {
            clonedSetting[foundSettingIndex][notif_type] = frequency_selected;
            // Update local state immediately for responsive UI
            setNotificationSettings(clonedSetting);
        }

        setProfileValues((prev: any) => ({
            ...prev,
            submit: true,
            data: {
                ...prev.data,
                notificationSetting: clonedSetting,
            },
        }));
    };

    return (
        <Fragment>
            <div className="section-group tw-py-2">
                <div className="d-flex justify-content-between gap-2">
                    <div className="section-label fw-bold">Notifications</div>
                </div>
            </div>

            {notificationSettings.map((el: any, i: number) => {
                const messageType = get(el, "messageType", "");
                const foundNotificationType = find(
                    SUPPORTED_MESSAGE_TYPES[gameVersion as keyof typeof SUPPORTED_MESSAGE_TYPES],
                    { message_type: messageType },
                );

                if (!foundNotificationType) return null;

                const message_type_label = get(foundNotificationType, "label", "");
                const email_frequency = get(el, "emailFrequency", "daily");
                const notification_frequencies = get(foundNotificationType, "supported_frequencies", "");
                const support_email = get(foundNotificationType, "support_email", false);
                const has_frequency_label = get(foundNotificationType, "has_frequency_label", false);
                const support_push = get(foundNotificationType, "support_push", false);
                const push_frequency = get(el, "pushFrequency", "daily");
                const action_type = get(el, "actionType", "");

                return (
                    <React.Fragment key={`fragment ${message_type_label + i}`}>
                        {action_type === "dropdown" ? (
                            <div className="mb-4" key={message_type_label + i}>
                                <div className="mb-2">{message_type_label}</div>
                                <div className="d-flex gap-3 ms-1 flex-wrap">
                                    {support_email && (
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span>Email:</span>
                                            <NotifAction
                                                notif_type="emailFrequency"
                                                index={`${i}-email`}
                                                frequency_selected={email_frequency}
                                                message_type={messageType}
                                                notification_frequencies={notification_frequencies}
                                                has_frequency_label={has_frequency_label}
                                                handleSetting={handleSetting}
                                                action_type={action_type}
                                            />
                                        </div>
                                    )}

                                    {support_push && (
                                        <div className="d-flex align-items-center gap-2">
                                            <span>Push:</span>
                                            <NotifAction
                                                notif_type="pushFrequency"
                                                index={`${i}-push`}
                                                frequency_selected={push_frequency}
                                                message_type={messageType}
                                                notification_frequencies={notification_frequencies}
                                                has_frequency_label={has_frequency_label}
                                                handleSetting={handleSetting}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div
                                className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
                                key={message_type_label + i}
                            >
                                <div className="tw-mb-2 tw-w-full">{message_type_label}</div>
                                <div className="d-flex justify-content-between gap-5">
                                    {support_email && (
                                        <div
                                            className={clsx(
                                                support_email && "ps-2 ps-sm-4",
                                                "d-flex align-items-center gap-3 lh-1",
                                            )}
                                        >
                                            {has_frequency_label && <span>Email:</span>}
                                            <NotifAction
                                                notif_type="emailFrequency"
                                                index={`${i}-email`}
                                                frequency_selected={email_frequency}
                                                message_type={messageType}
                                                notification_frequencies={notification_frequencies}
                                                has_frequency_label={has_frequency_label}
                                                handleSetting={handleSetting}
                                                action_type={action_type}
                                            />
                                        </div>
                                    )}

                                    {support_push && (
                                        <div
                                            className={clsx(
                                                support_push && "ps-4",
                                                "d-flex align-items-center gap-3 lh-1",
                                            )}
                                        >
                                            {has_frequency_label && <span>Push:</span>}
                                            <NotifAction
                                                notif_type="pushFrequency"
                                                index={`${i}-push`}
                                                frequency_selected={push_frequency}
                                                message_type={messageType}
                                                notification_frequencies={notification_frequencies}
                                                has_frequency_label={has_frequency_label}
                                                handleSetting={handleSetting}
                                                action_type={action_type}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </Fragment>
    );
};

export default NotificationSetting;
